import type { AppDispatch } from "_shared/_stores/store-types";
import EntityMember from "_shared/entity-members/entity-member";
import Guid from "_shared/guids/guid";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { showErrorSnackbar } from "_shared/_errors/error-snackbar";
import type { NavigateFunction } from "react-router";
import type Session from "users/__session/session";
import { PageTab } from "work/proposals/_views/drafts/draft-proposal-page-tabs";
import { openUnsavedDraft } from "work/proposals/_views/drafts/open-unsaved-draft";
import {
  buildDraftFromPreviousProposal,
  type DraftFromPreviousProposal,
} from "work/proposals/drafts-from-previous/draft-from-previous-proposal";
import type ProposalTeamReuse from "work/proposals/drafts-from-previous/proposal-team-reuse";
import type Proposal from "work/proposals/proposal";

/** The words used for a person whose name the reader's directory will not give. */
export const UNNAMED_PERSON = "a person you can no longer see";

const START_DRAFT_FAILURE = { doing: "starting a draft from this proposal", operation: "proposal.start-draft-from-previous" };

export type StartDraftFromPreviousProposalArgs = {
  proposal: Proposal;
  session: Session;
  dispatch: AppDispatch;
  navigate: NavigateFunction;
  /** Who on the proposal's teams cannot be carried over, as the server answers it now. */
  fetchTeamReuse: (proposalId: Guid) => Promise<ProposalTeamReuse>;
  /** Display names by user id, for telling the user who was left out. Best effort. */
  fetchNames: (userIds: Set<string>) => Promise<Map<string, string>>;
};

/**
 * Opens a new, unsaved draft pre-filled from `proposal` (#1569), the way a template opens one: the
 * builder is put where the create page reads it, and the page opens on Details.
 *
 * Nothing is saved. The user is told, in the same breath, what the draft could not take, so a person
 * left off a team never goes unnoticed.
 *
 * Returns false, and opens nothing, when who can be carried over could not be found out: a draft
 * carrying people who have left would be refused on its first save for a reason the user never saw.
 */
export async function startDraftFromPreviousProposal(args: StartDraftFromPreviousProposalArgs): Promise<boolean> {
  const { proposal, session, dispatch, navigate, fetchTeamReuse, fetchNames } = args;
  if (!proposal.id) throw new Error("A saved proposal is required to start a draft from it.");

  let teamReuse: ProposalTeamReuse;
  try {
    teamReuse = await fetchTeamReuse(proposal.id);
  } catch (error) {
    console.error("Failed to check who on the proposal can be carried over", error);
    showErrorSnackbar(error, START_DRAFT_FAILURE);
    return false;
  }

  let draft: DraftFromPreviousProposal;
  try {
    draft = buildDraftFromPreviousProposal({ proposal, session, teamReuse });
  } catch (error) {
    console.error("Failed to build a draft from a previous proposal", error);
    showErrorSnackbar(error, START_DRAFT_FAILURE);
    return false;
  }
  const names = await namesOf(draft, fetchNames);

  openUnsavedDraft({ builder: draft.builder, session, dispatch, navigate, pageTab: PageTab.Details });

  enqueueSnackbar(
    `Started a new draft from ${proposal.name?.value ?? "this proposal"}. Nothing is saved until you save it.`,
    { variant: "success" },
  );
  for (const notice of describeWhatWasLeftOut(draft, names)) {
    enqueueSnackbar(notice, { variant: "warning", persist: true });
  }
  return true;
}

/**
 * One sentence for each kind of thing the draft could not take, naming each one.
 *
 * Plain sentences and no counts alone: "2 people were dropped" sends the user hunting through two
 * rosters for who.
 */
export function describeWhatWasLeftOut(
  draft: Pick<DraftFromPreviousProposal, "peopleWhoLeft" | "leadersWhoCannotSign" | "unavailableDocuments">,
  names: Map<string, string>,
): string[] {
  const nameOf = (person: EntityMember) => names.get(person.userId.value) ?? UNNAMED_PERSON;
  const notices: string[] = [];

  if (draft.peopleWhoLeft.length > 0) {
    notices.push(
      `Left out ${listed(draft.peopleWhoLeft.map(nameOf))}, who ${
        draft.peopleWhoLeft.length === 1 ? "is" : "are"
      } no longer at ${draft.peopleWhoLeft.length === 1 ? "their organization" : "their organizations"}.`,
    );
  }
  for (const leader of draft.leadersWhoCannotSign) {
    notices.push(`Left out ${nameOf(leader)} as team leader, because they can no longer sign. Choose another leader.`);
  }
  if (draft.unavailableDocuments.length > 0) {
    const documentNames = draft.unavailableDocuments.map((document) => document.name.value);
    notices.push(
      `Did not copy ${listed(documentNames)}, which ${
        documentNames.length === 1 ? "no longer exists" : "no longer exist"
      }.`,
    );
  }
  return notices;
}

async function namesOf(
  draft: DraftFromPreviousProposal,
  fetchNames: (userIds: Set<string>) => Promise<Map<string, string>>,
): Promise<Map<string, string>> {
  const userIds = new Set(
    [...draft.peopleWhoLeft, ...draft.leadersWhoCannotSign].map((person) => person.userId.value),
  );
  if (userIds.size === 0) return new Map();
  try {
    return await fetchNames(userIds);
  } catch (error) {
    // The notice still says somebody was left out; only their name is missing.
    console.error("Failed to name the people left out of a copied draft", error);
    return new Map();
  }
}

function listed(items: string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
}
